import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, Col, Row, UncontrolledTooltip } from "reactstrap";
import { Tooltip } from "react-tooltip";
import { getFormattedDateString, hasPermission, Toast } from "utility/helpers";
import { Info, Copy, User, MapPin } from "react-feather";
import InputGroup from "components/customs/input-group";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendar,
  faCheck,
  faHandshake,
  faLocationArrow,
  faPen,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import PropTypes from "prop-types";
import { FILE_TYPE_IMAGE } from "constants/fileType";
import styled from "styled-components";
import { getIndex } from "./utility";

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)`
  margin-right: 23px;
  color: ${({ theme }) => theme.colors.red};
  font-size: 30px;
`;

const UserIcon = styled(User)`
  color: ${({ theme }) => theme.colors.red};
  width: 30px;
  height: 30px;
  margin-right: 20px;

  @media (max-width: 575.98px) {
    display: none;
  }
`;

const MapPinIcon = styled(MapPin)`
  color: ${({ theme }) => theme.colors.red};
  width: 30px;
  height: 30px;
  margin-right: 20px;

  @media (max-width: 575.98px) {
    display: none;
  }
`;

const ImageSection = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 575.98px) {
    align-items: center;
    margin-bottom: 10px;
  }
`;

const ImageContainer = styled.div`
  background: url(${(props) => props.img}) no-repeat;
  background-size: cover;
  background-position: top;
  border-radius: 33px;
  height: 200px;
  width: 200px;
`;

const FormValue = styled.span`
  font-weight: 400;
  color: ${({ theme }) => theme.colors.textBlue};
  text-align: end;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  margin-bottom: 8px;
`;

const FormLabel = styled.span`
  font-weight: 500;
`;

const EventLinkSection = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-bottom: 8px;
`;

const ButtonIcon = styled(Button)`
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.secondaryBlue};
  border: none;
`;

const EditButton = styled(ButtonIcon)`
  margin-left: 8px;
`;

const CongNameText = styled.h3`
  margin-bottom: 10px;
`;

const CongDetailsRow = styled(Row)`
  width: 100%;
  display: grid;
  grid-template-columns: 2fr 1fr;
  margin-top: 10px;

  @media (max-width: 575.98px) {
    grid-template-columns: 1fr;
  }
`;

const CongDetailsRight = styled.div`
  display: flex;
  flex-direction: column;
`;

const CongDetailsLeft = styled.div`
  gap: 16px;
  display: flex;
  flex-direction: row;
  border-right: 1px solid ${({ theme }) => theme.colors.borderGray};

  @media (max-width: 575.98px) {
    border-right: none;
    flex-direction: column;
  }
`;

const CongDetailsContainer = styled(Card)`
  padding: 20px;
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 25px;
  border: none;
  align-items: center;
  box-shadow: 4px 9px 17px -1px #00000014;
  margin-top: 44px;
`;

const ActionButton = styled(Button)`
  margin-left: 8px;
`;

const StyledTooltip = styled(Tooltip)`
  background-color: black;
`;

const TextMuted = styled.span`
  --bs-text-opacity: 1;
  color: #6c757d;
`;

const UpdatedByText = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const UpdatedByValue = styled.span`
  display: flex;
  justify-content: center;
`;

const FieldLabel = styled.div`
  display: flex;
  align-items: center;
`;

const FieldValue = styled(FieldLabel)`
  justify-content: flex-end;
`;

const CopyToClipboardContainer = styled.div`
  align-items: center;
  display: inline-flex;
`;

const CopyToClipboardNumber = styled.a`
  color: #00326e;
  text-decoration: none;
  cursor: pointer;
`;

const CopyToClipboardBtn = styled.button`
  border: none;
  background: none;
  padding: 0;
  margin-left: 0.5rem;
  color: ${({ theme }) => theme.colors.secondaryBlue};
`;

const CongressmanDetailsCard = ({
  congDetails,
  logs,
  isEditMode,
  setIsEditMode,
  tempCongDetails,
  setTempCongDetails,
  selectedRegion,
  regionOptions,
  setSelectedRegion,
  districtOptions,
  selectedDistrict,
  setSelectedDistrict,
  handleSaveButtonClicked,
  selectedParty,
  parties,
  setSelectedParty,
}) => {
  const navigate = useNavigate();

  const { id, congID } = useParams();

  const isHeaderActionsVisible = !!isEditMode || !!setIsEditMode;

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);

      Toast({
        type: 1,
        content: "Phone number copied to clipboard!",
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);

      Toast({
        type: 4,
        content: "Something went wrong.",
      });
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleFileUpload = (evt) => {
    const file = evt.target.files[0];
    const reader = new FileReader();
    const fileTye = file.type.split("/")[0];

    if (fileTye === FILE_TYPE_IMAGE) {
      reader.onloadend = () => {
        const imgContainer = document.querySelector(".cong-img");

        const fileName = congDetails
          ? `${
              congDetails.id
            }-${congDetails.lastName.toLocaleLowerCase()}-${Date.now()}`
          : `${congDetails.lastName.toLocaleLowerCase()}-${Date.now()}`;

        setTempCongDetails((prevState) => ({
          ...prevState,
          photo: file,
          photoName: fileName,
        }));
        imgContainer.src = reader.result;
      };

      reader.readAsDataURL(file);
    } else {
      Toast({
        type: 4,
        content: "File uploaded must be an image.",
      });
    }
  };

  const renderLogDetails = (key) => {
    let element = null;

    if (getIndex(key, logs) !== -1) {
      element = (
        <>
          <Info
            size={18}
            data-tooltip-id={key}
          />

          <StyledTooltip id={key}>
            <UpdatedByText>{"Updated By:"}</UpdatedByText>

            <UpdatedByValue>
              {logs[getIndex(key, logs)]?.createdBy}
            </UpdatedByValue>

            <UpdatedByValue>
              {`at ${getFormattedDateString(
                logs[getIndex(key, logs)]?.createdAt,
                "MMM dd, yyyy"
              )}`}
            </UpdatedByValue>
          </StyledTooltip>
        </>
      );
    }

    return element;
  };

  return (
    <CongDetailsContainer>
      {isHeaderActionsVisible ? (
        <EventLinkSection>
          {hasPermission("event-view") && (
            <>
              <UncontrolledTooltip target="event-link">
                {"Go to My Events"}
              </UncontrolledTooltip>
              <ButtonIcon
                id="event-link"
                onClick={() => {
                  navigate(`/representatives/${id}/cong/${congID}/event`, {
                    state: { repDetails: congDetails, congLogs: logs },
                  });
                }}
              >
                <FontAwesomeIcon icon={faCalendar} />
              </ButtonIcon>
            </>
          )}

          {!isEditMode ? (
            <>
              <UncontrolledTooltip target="edit-icon">
                {"Edit"}
              </UncontrolledTooltip>
              <EditButton
                id="edit-icon"
                onClick={() => {
                  setIsEditMode(!isEditMode);
                }}
              >
                <FontAwesomeIcon icon={faPen} />
              </EditButton>
            </>
          ) : (
            <>
              <ActionButton
                outline
                color="danger"
                onClick={() => {
                  setIsEditMode(!isEditMode);
                }}
              >
                <FontAwesomeIcon icon={faXmark} />
              </ActionButton>

              <ActionButton
                color="success"
                outline
                onClick={() => {
                  setIsEditMode(!isEditMode);
                  handleSaveButtonClicked();
                }}
              >
                <FontAwesomeIcon icon={faCheck} />
              </ActionButton>
            </>
          )}
        </EventLinkSection>
      ) : null}

      <CongDetailsRow>
        <CongDetailsLeft>
          <ImageSection>
            <ImageContainer img={congDetails?.photo} />
          </ImageSection>

          <Col>
            {isEditMode ? null : (
              <CongNameText>{congDetails?.fullName}</CongNameText>
            )}

            {isEditMode && (
              <>
                <FormRow>
                  <TextMuted>{"First Name"}</TextMuted>

                  <InputGroup
                    type="text"
                    value={tempCongDetails?.firstName}
                    handleOnChange={(e) => {
                      const { value } = e.target;

                      setTempCongDetails((prevState) => ({
                        ...prevState,
                        firstName: value,
                      }));
                    }}
                    handleClearInput={() => {
                      setTempCongDetails((prevState) => ({
                        ...prevState,
                        firstName: "",
                      }));
                    }}
                  />
                </FormRow>

                <FormRow>
                  <TextMuted>{"Middle Initial"}</TextMuted>

                  <InputGroup
                    type="text"
                    value={tempCongDetails?.middleInitial}
                    handleOnChange={(e) => {
                      const { value } = e.target;

                      setTempCongDetails((prevState) => ({
                        ...prevState,
                        middleInitial: value,
                      }));
                    }}
                    handleClearInput={() => {
                      setTempCongDetails((prevState) => ({
                        ...prevState,
                        middleInitial: "",
                      }));
                    }}
                  />
                </FormRow>
                <FormRow>
                  <TextMuted>{"Last Name"}</TextMuted>

                  <InputGroup
                    type="text"
                    value={tempCongDetails?.lastName}
                    handleOnChange={(e) => {
                      const { value } = e.target;

                      setTempCongDetails((prevState) => ({
                        ...prevState,
                        lastName: value,
                      }));
                    }}
                    handleClearInput={() => {
                      setTempCongDetails((prevState) => ({
                        ...prevState,
                        lastName: "",
                      }));
                    }}
                  />
                </FormRow>
              </>
            )}

            {congDetails?.District?.Region?.name !== "Partylist" ? (
              <FormRow>
                {isEditMode ? (
                  <TextMuted>{"Region"}</TextMuted>
                ) : (
                  <FieldLabel>
                    <MapPinIcon />
                    <FormLabel>{"Region"}</FormLabel>
                  </FieldLabel>
                )}

                {isEditMode ? (
                  <InputGroup
                    type="select"
                    value={selectedRegion}
                    options={regionOptions}
                    placeholder=""
                    handleOnChange={(option) => {
                      setSelectedRegion(option);
                    }}
                    handleClearInput={() => {
                      setSelectedRegion(null);
                    }}
                  />
                ) : (
                  <FieldValue>
                    <FormValue>
                      {congDetails?.District?.Region !== null
                        ? `${congDetails?.District?.Region?.acronym} - 
                                ${congDetails?.District?.Region?.name}`
                        : ""}
                    </FormValue>
                    {renderLogDetails("RegionId")}
                  </FieldValue>
                )}
              </FormRow>
            ) : null}

            <FormRow>
              {isEditMode ? (
                <TextMuted>
                  {congDetails?.District?.Region?.name !== "Partylist"
                    ? "District"
                    : "Partylist"}
                </TextMuted>
              ) : (
                <FieldLabel>
                  {congDetails?.District?.Region?.name !== "Partylist" ? (
                    <StyledFontAwesomeIcon icon={faLocationArrow} />
                  ) : (
                    <StyledFontAwesomeIcon icon={faHandshake} />
                  )}

                  <FormLabel>
                    {congDetails?.District?.Region?.name !== "Partylist"
                      ? "District"
                      : "Partylist"}
                  </FormLabel>
                </FieldLabel>
              )}

              {isEditMode ? (
                <InputGroup
                  type="select"
                  value={selectedDistrict}
                  options={districtOptions}
                  placeholder=""
                  handleOnChange={(option) => {
                    setSelectedDistrict(option);
                  }}
                  handleClearInput={() => {
                    setSelectedDistrict(null);
                  }}
                />
              ) : (
                <FieldValue>
                  <FormValue>{congDetails?.District?.name}</FormValue>
                  {renderLogDetails("DistrictId")}
                </FieldValue>
              )}
            </FormRow>

            <FormRow>
              {isEditMode ? (
                <TextMuted>{"Birthdate"}</TextMuted>
              ) : (
                <FieldLabel>
                  <UserIcon />
                  <FormLabel>{"Birthdate"}</FormLabel>
                </FieldLabel>
              )}

              {isEditMode ? (
                <InputGroup
                  type="date"
                  value={
                    tempCongDetails?.birthdate
                      ? moment(tempCongDetails.birthdate).format("MMM DD, YYYY")
                      : ""
                  }
                  handleOnChange={(dateString, dateStr) => {
                    const newDate = moment(dateStr).format("YYYY-MM-DD");

                    setTempCongDetails((prevState) => ({
                      ...prevState,
                      birthdate: newDate,
                    }));
                  }}
                  handleClearInput={() => {
                    setTempCongDetails((prevState) => ({
                      ...prevState,
                      birthdate: "",
                    }));
                  }}
                />
              ) : (
                <FieldValue>
                  <FormValue>
                    {tempCongDetails?.birthdate
                      ? moment(tempCongDetails?.birthdate).format(
                          "MMM DD, YYYY"
                        ) || null
                      : null}
                  </FormValue>
                  {renderLogDetails("birthdate")}
                </FieldValue>
              )}
            </FormRow>
          </Col>
        </CongDetailsLeft>

        <CongDetailsRight>
          <FormRow>
            {isEditMode ? (
              <TextMuted>{"Contact No."}</TextMuted>
            ) : (
              <FormLabel>{"Contact No."}</FormLabel>
            )}

            {isEditMode ? (
              <InputGroup
                type="mobile"
                value={tempCongDetails?.contact || ""}
                maxLength={11}
                handleOnChange={(value) => {
                  setTempCongDetails((prevState) => ({
                    ...prevState,
                    contact: value,
                  }));
                }}
                handleClearInput={() => {
                  setTempCongDetails((prevState) => ({
                    ...prevState,
                    contact: "",
                  }));
                }}
              />
            ) : (
              <FieldValue>
                {renderLogDetails("contact")}

                {congDetails?.contact ? (
                  <CopyToClipboardContainer>
                    <CopyToClipboardNumber
                      href={`tel:+63${congDetails.contact.substring(1)}`}
                      title="Click to call"
                    >
                      {congDetails.contact}
                    </CopyToClipboardNumber>
                    <CopyToClipboardBtn
                      type="button"
                      onClick={() => copyToClipboard(congDetails.contact)}
                      title="Copy to clipboard"
                    >
                      <Copy size={14} />
                    </CopyToClipboardBtn>
                  </CopyToClipboardContainer>
                ) : null}
              </FieldValue>
            )}
          </FormRow>

          {congDetails?.District?.Region?.name !== "Partylist" ? (
            <FormRow>
              {isEditMode ? (
                <TextMuted>{"Party"}</TextMuted>
              ) : (
                <FormLabel>{"Party"}</FormLabel>
              )}

              {isEditMode ? (
                <InputGroup
                  type="select"
                  value={selectedParty}
                  options={parties}
                  handleOnChange={(option) => {
                    setSelectedParty(option);
                  }}
                  handleClearInput={() => {
                    setSelectedParty(null);
                  }}
                />
              ) : (
                <FieldValue>
                  <FormValue>{congDetails?.Party?.acronym}</FormValue>
                  {renderLogDetails("PartyId")}
                </FieldValue>
              )}
            </FormRow>
          ) : null}

          <FormRow>
            {isEditMode ? (
              <TextMuted>{"Term of Office"}</TextMuted>
            ) : (
              <FormLabel>{"Term of Office"}</FormLabel>
            )}

            {isEditMode ? (
              <InputGroup
                type="text"
                value={tempCongDetails?.termOfOffice}
                handleOnChange={(e) => {
                  const { value } = e.target;

                  setTempCongDetails((prevState) => ({
                    ...prevState,
                    termOfOffice: value,
                  }));
                }}
                handleClearInput={() => {
                  setTempCongDetails((prevState) => ({
                    ...prevState,
                    termOfOffice: "",
                  }));
                }}
              />
            ) : (
              <FieldValue>
                <FormValue>{congDetails?.termOfOffice}</FormValue>
                {renderLogDetails("termOfOffice")}
              </FieldValue>
            )}
          </FormRow>

          <FormRow>
            {isEditMode ? (
              <TextMuted>{"Chief of Staff First Name"}</TextMuted>
            ) : (
              <FormLabel>{"Chief of Staff"}</FormLabel>
            )}

            {isEditMode ? (
              <InputGroup
                type="text"
                value={tempCongDetails?.cosFirstName}
                handleOnChange={(e) => {
                  const { value } = e.target;

                  setTempCongDetails((prevState) => ({
                    ...prevState,
                    cosFirstName: value,
                  }));
                }}
                handleClearInput={() => {
                  setTempCongDetails((prevState) => ({
                    ...prevState,
                    cosFirstName: "",
                  }));
                }}
              />
            ) : (
              <FieldValue>
                <FormValue>{congDetails?.cosName}</FormValue>
                {renderLogDetails("cosName")}
              </FieldValue>
            )}
          </FormRow>

          {isEditMode && (
            <FormRow>
              <TextMuted>{"Chief of Staff Last Name"}</TextMuted>

              <InputGroup
                type="text"
                value={tempCongDetails?.cosLastName}
                handleOnChange={(e) => {
                  const { value } = e.target;

                  setTempCongDetails((prevState) => ({
                    ...prevState,
                    cosLastName: value,
                  }));
                }}
                handleClearInput={() => {
                  setTempCongDetails((prevState) => ({
                    ...prevState,
                    cosLastName: "",
                  }));
                }}
              />
            </FormRow>
          )}

          <FormRow>
            {isEditMode ? (
              <TextMuted>{"COS Contact No."}</TextMuted>
            ) : (
              <FormLabel>{"COS Contact No."}</FormLabel>
            )}

            {isEditMode ? (
              <InputGroup
                type="mobile"
                value={tempCongDetails?.cosContact || ""}
                maxLength={11}
                handleOnChange={(value) => {
                  setTempCongDetails((prevState) => ({
                    ...prevState,
                    cosContact: value,
                  }));
                }}
                handleClearInput={() => {
                  setTempCongDetails((prevState) => ({
                    ...prevState,
                    cosContact: "",
                  }));
                }}
              />
            ) : (
              <FieldValue>
                {renderLogDetails("cosContact")}

                {congDetails?.cosContact ? (
                  <CopyToClipboardContainer>
                    <CopyToClipboardNumber
                      href={`tel:+63${congDetails.cosContact.substring(1)}`}
                      title="Click to call"
                    >
                      {congDetails.cosContact}
                    </CopyToClipboardNumber>
                    <CopyToClipboardBtn
                      type="button"
                      onClick={() => copyToClipboard(congDetails.cosContact)}
                      title="Copy to clipboard"
                    >
                      <Copy size={14} />
                    </CopyToClipboardBtn>
                  </CopyToClipboardContainer>
                ) : null}
              </FieldValue>
            )}
          </FormRow>
        </CongDetailsRight>
      </CongDetailsRow>
    </CongDetailsContainer>
  );
};

export default CongressmanDetailsCard;

CongressmanDetailsCard.propTypes = {
  isEditMode: PropTypes.bool,
  setIsEditMode: PropTypes.func,
};
